/** A continuous visual fill for the PDP's existing mobile gallery controls. */
class LazyOafPdpGalleryProgress extends HTMLElement {
  #galleryObserver;
  #sizeObserver;
  #scrollEvents;
  #scroller;
  #dots;
  #frame;

  connectedCallback() {
    const container = this.parentElement;
    if (!container) return;

    this.#galleryObserver = new MutationObserver(() => this.#bind(container));
    this.#galleryObserver.observe(container, { childList: true, subtree: true });
    this.#bind(container);
  }

  disconnectedCallback() {
    this.#galleryObserver?.disconnect();
    this.#scrollEvents?.abort();
    this.#sizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    this.#scroller = undefined;
    this.#dots = undefined;
  }

  #bind(container) {
    const gallery = container.querySelector('media-gallery');
    const scroller = gallery?.querySelector('slideshow-slides');
    const dots = gallery?.querySelector('.media-gallery__mobile-controls .slideshow-controls__dots');
    if (scroller === this.#scroller && dots === this.#dots) return;

    this.#scrollEvents?.abort();
    this.#sizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    this.#scroller = scroller;
    this.#dots = dots;
    if (!scroller || !dots) return;

    this.#scrollEvents = new AbortController();
    scroller.addEventListener('scroll', this.#schedule, { passive: true, signal: this.#scrollEvents.signal });
    this.#sizeObserver = new ResizeObserver(this.#schedule);
    this.#sizeObserver.observe(scroller);
    for (const slide of scroller.children) this.#sizeObserver.observe(slide);
    this.#update();
    dots.dataset.progressReady = '';
  }

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #update() {
    const scroller = this.#scroller;
    const dots = this.#dots;
    if (!scroller || !dots) return;

    const total = scroller.scrollWidth;
    const visible = scroller.clientWidth;
    const filled = total > 0 ? Math.min(1, Math.max(0, (Math.abs(scroller.scrollLeft) + visible) / total)) : 1;
    dots.style.setProperty('--lazy-oaf-gallery-progress', filled);
  }
}

if (!customElements.get('lazy-oaf-pdp-gallery-progress')) {
  customElements.define('lazy-oaf-pdp-gallery-progress', LazyOafPdpGalleryProgress);
}
